/**
 * Which LibertySwap backend the swap stack talks to.
 *
 * There are FOUR bases, and every one of them is configured by the app from
 * `.env` — nothing is hardcoded here:
 *
 * | base    | `.env` key         | serves                                           |
 * |---------|--------------------|--------------------------------------------------|
 * | root    | `LBS_CCTP_API_URL` | CCTP endpoints, `/railgun/shorten`               |
 * | app     | `LBS_API_URL`      | quote, routers, orders, tx/process, encrypt      |
 * | gasless | `GASLESS_API_URL`  | the gasless relayer: submit + status             |
 * | dex     | `LBS_DEX_API_URL`  | PulseChain Permit2 solver: config, quote, orders |
 *
 * They must all come from the SAME deployment: the router registry, the quote
 * pricing and the relayer are one system, and the current contracts take a 6th
 * `integratorId` argument, so calldata built for them reverts on a router from
 * an environment that still runs the old ABI. Mixing hosts is how that happens.
 *
 * This module is env-agnostic (submodule rule), so it never reads `process.env`
 * itself; `common/config/env/env.ts` pushes the values in at boot. There is no
 * built-in default: LibertySwap versions and moves these hosts on their own
 * schedule (production went to `apis.libertyswap.finance/v3` on 2026-08-19), and
 * a stale hardcoded host is worse than a loud failure — the getters throw and
 * name the missing key rather than composing a request off an empty base.
 */
export type LibertyApiEnv = 'dev' | 'uat' | 'prod'

type LibertyApiBase = 'root' | 'app' | 'gasless' | 'dex'

/** Only used to name the missing key in the error. */
const ENV_KEYS: Record<LibertyApiBase, string> = {
  root: 'LBS_CCTP_API_URL',
  app: 'LBS_API_URL',
  gasless: 'GASLESS_API_URL',
  dex: 'LBS_DEX_API_URL'
}

/**
 * The environment no longer selects a host, but it still selects the gasless
 * relayer contract and the integrator id (`gasless.ts` / `integrator.ts`), both
 * of which end up inside what the user signs. Keep it in step with the bases.
 */
let libertyApiEnv: LibertyApiEnv = 'prod'

const configuredBases: Record<LibertyApiBase, string | null> = {
  root: null,
  app: null,
  gasless: null,
  dex: null
}

/** Call once at boot, before any swap/quote/router call. */
export function setLibertyApiEnv(env: LibertyApiEnv): void {
  libertyApiEnv = env
}

export function getLibertyApiEnv(): LibertyApiEnv {
  return libertyApiEnv
}

const normalizeBase = (url: string | null | undefined): string | null => {
  const trimmed = typeof url === 'string' ? url.trim() : ''
  if (!trimmed) return null

  // Callers configure a base, not a directory; a trailing slash would produce
  // `//quote` once a path is appended.
  return trimmed.replace(/\/+$/, '')
}

const resolveBase = (base: LibertyApiBase): string => {
  const configured = configuredBases[base]
  if (!configured)
    throw new Error(
      `LibertySwap ${base} API base is not configured — set ${ENV_KEYS[base]} in .env`
    )

  return configured
}

/** Configure the root base — `LBS_CCTP_API_URL`. */
export function setLibertyApiBase(url: string | null | undefined): void {
  configuredBases.root = normalizeBase(url)
}

/** Configure the `/app` base — `LBS_API_URL`. */
export function setLibertyAppApiBase(url: string | null | undefined): void {
  configuredBases.app = normalizeBase(url)
}

/** Configure the gasless base — `GASLESS_API_URL`. */
export function setGaslessApiBase(url: string | null | undefined): void {
  configuredBases.gasless = normalizeBase(url)
}

/** Configure the DEX base — `LBS_DEX_API_URL`. */
export function setLibertyDexApiBase(url: string | null | undefined): void {
  configuredBases.dex = normalizeBase(url)
}

/** Host root — CCTP and `/railgun/shorten` hang off this. */
export function getLibertyApiBase(): string {
  return resolveBase('root')
}

/** The `/app` namespace: quote, routers, orders, tx/process, addresses/encrypt. */
export function getLibertyAppApiBase(): string {
  return resolveBase('app')
}

/** The gasless relayer namespace: submit + status. */
export function getLibertyGaslessApiBase(): string {
  return resolveBase('gasless')
}

/** PulseChain DEX (Permit2 solver) config + orders. */
export function getLibertyDexApiBase(): string {
  return resolveBase('dex')
}
